import { Checkbox, Dropdown, DropdownChip, DropdownContent, DropdownItem, DropdownTrigger } from '@mesh.ai/rabbit';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

export type MultiSelectWithCheckboxesOption = {
  value: string;
  label: string;
};

export type MultiSelectWithCheckboxesElementProps = {
  dropdown?: React.ComponentProps<typeof Dropdown>;
  dropdownTrigger?: React.ComponentProps<typeof DropdownTrigger>;
  dropdownChip?: React.ComponentProps<typeof DropdownChip>;
  dropdownContent?: React.ComponentProps<typeof DropdownContent>;
  dropdownItem?: React.ComponentProps<typeof DropdownItem>;
  labelText?: React.ComponentProps<typeof TextBodyMedium>;
  displayText?: React.ComponentProps<typeof TextBodyMedium>;
};

export type MultiSelectWithCheckboxesProps = {
  /** Array of options to display in the dropdown */
  options: MultiSelectWithCheckboxesOption[];
  /** Array of currently selected options */
  selectedOptions: MultiSelectWithCheckboxesOption[];
  /** Callback function when selection changes */
  onChange: (selectedOptions: MultiSelectWithCheckboxesOption[]) => void;
  /** Label text to display before the selected values */
  label: string;
  /** Placeholder text when no items are selected */
  placeholder?: string;
  /** Maximum number of items to show before truncating (default: 2) */
  maxDisplayItems?: number;
  /** Whether to show the "Select All" option (default: true) */
  showSelectAll?: boolean;
  /** Props to pass to individual elements */
  elementProps?: MultiSelectWithCheckboxesElementProps;
};

export const MultiSelectWithCheckboxes = ({
  options,
  selectedOptions,
  onChange,
  label,
  placeholder = 'Select',
  maxDisplayItems = 2,
  showSelectAll = true,
  elementProps = {},
}: MultiSelectWithCheckboxesProps) => {
  const {
    dropdown = {},
    dropdownTrigger = {},
    dropdownChip = {},
    dropdownContent = {},
    dropdownItem = {},
    labelText = {},
    displayText = {},
  } = elementProps;

  const allOptionsSelected = options.length > 0 && selectedOptions.length === options.length;

  const handleSelectAll = () => {
    if (allOptionsSelected) {
      // Deselect all
      onChange([]);
    } else {
      // Select all
      onChange([...options]);
    }
  };

  const handleCheckedChange = (optionValue: string) => {
    const option = options.find((opt) => opt.value === optionValue);
    if (!option) return;

    const isSelected = selectedOptions.some((selected) => selected.value === optionValue);

    if (isSelected) {
      onChange(selectedOptions.filter((selected) => selected.value !== optionValue));
    } else {
      onChange([...selectedOptions, option]);
    }
  };

  const getDisplayContent = () => {
    if (selectedOptions.length === 0) {
      return (
        <TextBodyMedium bold color="text.g3" {...displayText}>
          {placeholder}
        </TextBodyMedium>
      );
    }

    // Sort selected options based on original options order
    const sortedSelectedOptions = options.filter((option) =>
      selectedOptions.some((selected) => selected.value === option.value)
    );

    if (sortedSelectedOptions.length > maxDisplayItems) {
      const visibleItems = sortedSelectedOptions
        .slice(0, maxDisplayItems)
        .map((option) => option.label)
        .join(', ');
      const remainingCount = sortedSelectedOptions.length - maxDisplayItems;

      return (
        <>
          <TextBodyMedium bold color="text.g1" {...displayText}>
            {visibleItems},
          </TextBodyMedium>
          <TextBodyMedium bold color="#0D819F" {...displayText}>
            {` +${remainingCount}`}
          </TextBodyMedium>
        </>
      );
    }

    const displayTextContent = sortedSelectedOptions.map((option) => option.label).join(', ');

    return (
      <TextBodyMedium bold color="text.g1" {...displayText}>
        {displayTextContent}
      </TextBodyMedium>
    );
  };

  return (
    <Dropdown {...dropdown}>
      <DropdownTrigger asChild {...dropdownTrigger}>
        <DropdownChip {...dropdownChip}>
          <TextBodyMedium bold color="text.g2" {...labelText}>
            {label} :
          </TextBodyMedium>
          {getDisplayContent()}
        </DropdownChip>
      </DropdownTrigger>
      <DropdownContent
        align="end"
        style={{ width: '200px', maxHeight: '400px', overflowY: 'auto' }}
        {...dropdownContent}
      >
        {showSelectAll && options.length > 0 && (
          <DropdownItem
            onSelect={handleSelectAll}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            {...dropdownItem}
          >
            <Checkbox checked={allOptionsSelected} />
            Select All
          </DropdownItem>
        )}
        {options.map((option) => (
          <DropdownItem
            key={option.value}
            onSelect={() => handleCheckedChange(option.value)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            {...dropdownItem}
          >
            <Checkbox checked={selectedOptions.some((selected) => selected.value === option.value)} />
            {option.label}
          </DropdownItem>
        ))}
      </DropdownContent>
    </Dropdown>
  );
};
